import './titleScreen.css';
import { createPlateLayer } from './plateLayer.js';
import { formatNamesCarved } from './introTimeline.js';
import { getSettings } from '../settings/store.js';

const TITLE_TEXT = 'BLACK MARBLE';

const esc = (value: string): string =>
  value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

const glyphMarkup = (text: string): string => {
  let glyph = 0;
  return text
    .split('')
    .map((char) => {
      if (char === ' ') return `<span class="ts-gap" aria-hidden="true"> </span>`;
      const span = `<span class="ts-glyph" style="--glyph-index:${glyph}">${esc(char)}</span>`;
      glyph++;
      return span;
    })
    .join('');
};

// Owner ruling (Wave 1 parity): the TITLE plate carries the MONUMENT's fixed
// count — "1,247,309,544 NAMES CARVED" — matching the reference cinematic. The
// player's personal ledger count stays in the main-menu folio footer only.

export class TitleScreen {
  private readonly root: HTMLDivElement;
  private readonly state: HTMLDivElement;
  private open = false;
  private onDismiss: (() => void) | null = null;

  constructor() {
    this.root = document.createElement('div');
    this.root.id = 'title-screen';
    this.root.innerHTML = `
      <div class="ts-mark" aria-label="${TITLE_TEXT}">${glyphMarkup(TITLE_TEXT)}</div>
      <div class="ts-state"></div>`;
    this.root.prepend(
      createPlateLayer({
        id: 'bm-title-plate',
        className: 'ts-plate',
        src: '/assets/ui/plate-title.png',
      }),
    );
    this.root.style.display = 'none';
    document.body.appendChild(this.root);
    this.state = this.root.querySelector('.ts-state') as HTMLDivElement;
    this.root.addEventListener('click', () => this.dismiss());
    window.addEventListener('keydown', (event) => this.onKey(event));
  }

  show(onDismiss: () => void, liveUnderlay = false): void {
    this.open = true;
    this.onDismiss = onDismiss;
    this.state.textContent = formatNamesCarved();
    // Wave 1 handoff parity: when the intro's live engine frame persists
    // beneath, drop the static plate + skip the second glyph carve (the intro
    // already carved the title once — it must not replay).
    this.root.classList.toggle('live', liveUnderlay);
    this.root.classList.toggle('reduced', getSettings().accessibility.reduceMotion);
    this.root.classList.remove('shown');
    this.root.style.display = 'flex';
    requestAnimationFrame(() => this.root.classList.add('shown'));
  }

  hide(): void {
    this.open = false;
    this.onDismiss = null;
    this.root.classList.remove('shown');
    this.root.style.display = 'none';
  }

  dispose(): void {
    this.hide();
    this.root.remove();
  }

  private dismiss(): void {
    if (!this.open) return;
    const cb = this.onDismiss;
    this.hide();
    cb?.();
  }

  private onKey(event: KeyboardEvent): void {
    if (!this.open) return;
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Escape') {
      event.preventDefault();
      this.dismiss();
    }
  }
}
